import { Component, Input, OnDestroy, OnInit } from "@angular/core";
import { Observable, Subscription, forkJoin, of, catchError, retry } from "rxjs";
import { BoydRewardsService } from "src/app/services/boyd-rewards/boyd-rewards.service";
import { ConfigService } from "src/app/services/config/config.service";
import { EventsService } from "src/app/services/event/event.service";
import { GameHubControllerService } from "src/app/services/gameHub/game-hub-controller";
import { RewardsService } from "src/app/services/rewards/rewards.service";
import { Navigator } from "src/app/services/routing/navigator";
import { BonusService } from "src/app/services/bonus/bonus.service";
import { LoadingController } from "@ionic/angular";
import { GameTileService } from "src/app/services/game-tile/game-tile.service";
import { StructureHelperService } from "src/app/services/structure/structure-helper.service";
import { RoutingService } from "src/app/services/routing/routing.service";

interface GameData {
  vendor: string;
  extGameType: string;
  name: string;
  id: number;
  imagename: string;
  vendor_name: string;
  game_type_id: number;
  super_type: string;
  wager_bonus_contribution: number;
  rtp: number;
}

interface BonusGame {
  freeBetAmount: number;
  games: GameData[];
  freeSpinId: string;
  campaignId: number;
  campaignCode: string;
  campaignName: string;
  gameDataArray?: GameData[];
}

interface GamesWithBonus {
  [vendor: string]: BonusGame[];
}

@Component({
  selector: "app-games-bonus-free-spins",
  templateUrl: "./games-bonus-free-spins.component.html",
  styleUrls: ["./games-bonus-free-spins.component.scss"],
})
export class GamesBonusFreeSpinsComponent implements OnInit, OnDestroy {
  @Input() freeSpinsEmpty = false;
  public gamesWithBonus: GamesWithBonus;
  public imagePath = "";
  public numOfGamesThreshold = 8;
  private gameSubscriptions: Subscription;
  public internalGames: any[] = [];
  public loading: any;
  public externalLoadingDone: boolean = false;
  public internalLoadingDone: boolean = false;
  public showFreeSpinCampaignName: boolean;
  public displayEmpty: Boolean = false;

  constructor(
    private bonusService: BonusService,
    private boydRewardsService: BoydRewardsService,
    private config: ConfigService,
    private events: EventsService,
    private gameHubController: GameHubControllerService,
    private gameHubControllerService: GameHubControllerService,
    private gameTileService: GameTileService,
    private loadingController: LoadingController,
    private navigator: Navigator,
    private rewardsService: RewardsService,
    private routing: RoutingService,
    private structureHelper: StructureHelperService,
  ) {
    this.imagePath = this.gameTileService.galleryImagePath;
    if (this.imagePath && !this.imagePath.endsWith('/')) {
      this.imagePath += '/';
    }
    this.showLoading();
    this.getExternalBonusFreeSpins();
    this.getInternalBonusFreeSpins();
  }

  ngOnInit() {
    this.numOfGamesThreshold = !isNaN(Number(this.config.getConfig().FEATURE_GAME_BONUS_FREE_NUMBER_THRESHOLD)) ? Number(this.config.getConfig().FEATURE_GAME_BONUS_FREE_NUMBER_THRESHOLD) : 8;
    this.showFreeSpinCampaignName = this.config.getConfig().FEATURE_SHOW_FREE_SPIN_CAMPAIGN_NAME === '1' ? true : false;
  }

  private getInternalBonusFreeSpins() {
    this.rewardsService
      .getInternalFreeSpins()
      .subscribe((response) => {

        if (response.result == 1) {
          response.payload.forEach(element => {
            let numSpins = parseInt(element.numFreeSpins);
            let numLines = parseInt(element.numPaylines);
            const promoId = element.campaignId;
            const promoName = element.campaignName;
            let betPerLine = Math.round(parseFloat(element.betPerLine) * 100);
            let currency = element.type;
            let expiryDate = element.expiryDate;

            let bonusFreeSpin = { numSpins: numSpins, numLines: numLines, campaignId: promoId, campaignName: promoName, bet: { amount: betPerLine, currency: currency }, expiryDate: expiryDate, games: [] }
            if (element.launch_ids != "any") {
              let launch_ids = element.launch_ids.split(",");
              let game_type_ids = element.game_type_ids.split(",");
              let images = element.imagenames.split(",");
              let names = element.game_names.split(",");

              for (let i = 0; i < launch_ids.length; i++) {
                let game = { launch_id: launch_ids[i], game_type_id: game_type_ids[i], game_name: names[i], imagename: images[i] }
                bonusFreeSpin.games.push(game);
              }

            }

            this.internalGames.push(bonusFreeSpin);
            this.checkGameListEmpty();
          });
        }


        this.loadingDoneInternal();
      });
  }


  private getExternalBonusFreeSpins() {
    this.boydRewardsService
      .callGetExternalGamesBonusSpins()
      .subscribe({
        next: (resp) => {
          this.gamesWithBonus = resp.payload.freeBetInfo;

          const parallelObservableArray: Observable<GameData[]>[] = [];

          // Pre-process games data
          for (const vendor in this.gamesWithBonus) {
            for (const game of this.gamesWithBonus[vendor]) {
              // Ensure games array exists and has data
              if (!game.games || !Array.isArray(game.games)) {
                game.games = [];
              }

              // Don't filter if number of games is not more than threshold number
              if (game.games.length <= this.numOfGamesThreshold) {
                parallelObservableArray.push(
                  this.structureHelper.filterGamesWithListBasedOnIds(game.games)
                    .pipe(
                      // Add error handling for each request
                      catchError(error => {
                        console.error('Error loading game data:', error);
                        return of([]);
                      })
                    )
                );
              } else {
                parallelObservableArray.push(of([]));
              }
            }
          }

          // Only proceed with forkJoin if we have observables to process
          if (parallelObservableArray.length > 0) {
            this.gameSubscriptions = forkJoin(parallelObservableArray)
              .pipe(
                // Add retry logic
                retry(1),
                // Ensure we complete even if there's an error
                catchError(error => {
                  console.error('Error in forkJoin:', error);
                  return of([]);
                })
              )
              .subscribe({
                next: (data) => {
                  let gameIndex = 0;
                  for (const vendor in this.gamesWithBonus) {
                    for (const game of this.gamesWithBonus[vendor]) {
                      // Ensure we have valid data before assigning
                      if (data[gameIndex]) {
                        game.gameDataArray = data[gameIndex];
                        // Pre-load images
                        if (game.gameDataArray?.length) {
                          game.gameDataArray.forEach(gameData => {
                            if (gameData.imagename) {
                              const img = new Image();
                              img.src = this.imagePath + gameData.imagename;
                            }
                          });
                        }
                      }
                      gameIndex++;
                    }
                  }
                  this.loadingDoneExternal();
                  this.checkGameListEmpty();
                },
                error: (error) => {
                  console.error('Error processing game data:', error);
                  this.loadingDoneExternal();
                  this.checkGameListEmpty();
                }
              });
          } else {
            this.loadingDoneExternal();
            this.checkGameListEmpty();
          }
        },
        error: (error) => {
          console.error('Error fetching bonus free spins:', error);
          this.loadingDoneExternal();
          this.checkGameListEmpty();
        }
      });
  }

  protected launchGameWithData(gameData) {
    // console.log(" Game data ", this.gameService.getGameById(1418));
    // const gameData = this.gameService.getGameById(gameId);
    this.closeGame();
    this.onFreeSpinModalCancel();
    setTimeout(() => {
      this.gameHubControllerService.launchCasinoGame(gameData);
    }, 1000);
  }


  onFreeSpinModalCancel() {
    this.navigator.dismiss();
  }

  closeGame() {
    this.events.publish("event:game:close");
    this.gameHubController.killAllInstances();
  }

  protected launchGameWithId(gameId) {
    this.closeGame();
    const gameData = this.structureHelper.getGameById(gameId);
    this.onFreeSpinModalCancel();
    setTimeout(() => {
      this.gameHubControllerService.launchCasinoGame(gameData);
    }, 1000);
  }

  goBackToPromotion(gamesInfo) {
    this.bonusService.setPromoDetailInfo({ id: gamesInfo.campaignId, listType: "IN_PROGRESS" });
    this.navigator.dismissAll('');
    this.routing.toPromotionsTab();
  }

  ngOnDestroy(): void {
    if (this.gameSubscriptions) {
      this.gameSubscriptions.unsubscribe();
    }
  }

  loadingDoneExternal() {
    this.externalLoadingDone = true;
    this.loadingDone();
  }

  loadingDoneInternal() {
    this.internalLoadingDone = true;
    this.loadingDone();
  }

  loadingDone() {
    if (this.internalLoadingDone && this.externalLoadingDone) this.loading.dismiss();
  }

  async showLoading() {
    this.loading = await this.loadingController.create({
      message: 'Loading...',
      duration: 10000
    });

    this.loading.present();
  }

  checkGameListEmpty() {
    if (!this.internalGames.length && Object.keys(this.gamesWithBonus).length == 0 && this.freeSpinsEmpty) {
      this.displayEmpty = true
    }
  }
}
